import React, { useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import { Modal } from "../../../../components";
import locale from "../../../../constants/locale";
import { useTranslation } from "react-i18next";

const generateUniqueFileName = (extension: string = "jpg"): string => {
  // Generate a timestamp-based unique file name
  const timestamp = new Date().toISOString().replace(/[-:.]/g, "");
  return `image_${timestamp}.${extension}`;
};

const WebcamCapture = ({
  onClose,
  onChange,
}: {
  onClose: () => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  const { t } = useTranslation();
  const webcamRef = useRef<Webcam>(null);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string | undefined>(undefined);
  const [image, setImage] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);

  // Capture an image using the webcam and convert it to a Blob
  const capture = () => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        setImage(imageSrc);
        convertToBlob(imageSrc);
      }
    }
  };

  // Convert base64 image to Blob
  const convertToBlob = (base64Image: string) => {
    fetch(base64Image)
      .then((res) => res.arrayBuffer())
      .then((buffer) => {
        const newBlob = new Blob([buffer], { type: "image/jpeg" });
        setBlob(newBlob);
      })
      .catch((error) => console.error("Error converting to Blob:", error));
  };

  // Trigger the parent component's handleFileChange with the selected Blob
  const handleUpload = () => {
    if (blob) {
      const fileName = generateUniqueFileName("jpg"); // Generate a unique file name
      const file = new File([blob], fileName, {
        type: "image/jpeg",
      });
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);

      const syntheticEvent = {
        target: {
          files: dataTransfer.files,
        },
      } as React.ChangeEvent<HTMLInputElement>;

      onChange(syntheticEvent);
      onClose();
    }
  };

  // Get available video devices
  useEffect(() => {
    const fetchVideoDevices = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(
        (device) => device.kind === "videoinput"
      );
      setVideoDevices(videoDevices);

      // Set the default video device (usually the front camera)
      if (videoDevices.length > 0) {
        setDeviceId(videoDevices[0].deviceId);
      }
    };

    fetchVideoDevices();
  }, []);

  // Switch to the next available video device
  const switchCamera = () => {
    if (videoDevices.length > 1) {
      const currentIndex = videoDevices.findIndex(
        (device) => device.deviceId === deviceId
      );
      const nextIndex = (currentIndex + 1) % videoDevices.length;
      setDeviceId(videoDevices[nextIndex].deviceId);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Capture"
      disablePrimary={!image}
      primaryText={t(locale.upload)}
      onPrimary={handleUpload}
    >
      <div className="is-flex mb-4">
        <button className="button is-primary is-outlined" onClick={capture}>
          {t(locale.capture)}
        </button>
        <input
          type="file"
          accept="image/jpeg,image/png,video/mp4"
          multiple
          onChange={(e) => {
            onChange(e);
            onClose();
          }}
          className="is-hidden"
          id="upload-image"
        />
        <button className="ml-2 button is-primary is-outlined">
          <label htmlFor="upload-image">{t(locale.browse)}</label>
        </button>
      </div>

      {image && (
        <div>
          <h3>{t(locale.captureResult)}</h3>
          <img
            src={image}
            alt="Captured"
            style={{ width: "100%", height: "auto" }}
          />
        </div>
      )}

      {/* Only show webcam when capturing, hide when browsing */}
      {!image && (
        <>
          {videoDevices.length > 1 && (
            <button className="button is-small mb-4" onClick={switchCamera}>
              {t(locale.switchCamera)}
            </button>
          )}
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            videoConstraints={{
              deviceId: deviceId ? { exact: deviceId } : undefined,
            }}
            width="100%"
          />
        </>
      )}
    </Modal>
  );
};

export default WebcamCapture;
